<script>
import { Helper, debounce } from '@/mixin/helper'
import moment from 'moment'
import * as halosisWhatsappLogApi from '@/services/rest-api-sms/lib/halosis-whatsapp-log/halosis-whatsapp-log'
import * as emailLogApi from '@/services/rest-api-sms/lib/email-log/email-log'

export default {
  name: 'EmailLog',
  props: {
    modal: {
      type: Boolean,
      required: true,
    },
  },

  mixins: [Helper],

  data() {
    const todayStart = moment().startOf('day').toDate()
    const todayEnd = moment().endOf('day').toDate()
    return {
      totalPages: 0,
      isModalOpen: false,
      loading: false,
      filter: '',
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      filteredItems: [],
      emailLogs: [],
      selectedServerRegion: 'SG',
      fields: [
        { key: 'created_at', label: 'Date' },
        { key: 'email_type', label: 'Type' },
        { key: 'username', label: 'Username' },
        { key: 'email', label: 'Email / Phone Number' },
        { key: 'link', label: 'Link' },
        { key: 'domain', label: 'Domain' },
        { key: 'email_status', label: 'Status' },
        { key: 'expired_status', label: 'Expired Status' },
      ],
      dateRange: [todayStart, todayEnd],

      whatsappLogFields: [
        { key: 'data_timestamp', label: 'Date' },
        { key: 'fullname', label: 'Username' },
        { key: 'target_phone_num', label: 'Phone No' },
        { key: 'domain_name', label: 'Domain' },
        { key: 'template_name', label: 'Template Name' },
        { key: 'status', label: 'Status' },
      ],

      totalPagesWhatsapp: 0,
      currentPageWhatsapp: 1,
      perPageWhatsapp: 10,
      totalRowsWhatsapp: 0,
      pageOptionsWhatsapp: [10, 25, 50, 100],
      filteredItemsWhatsapp: [],
      whatsappLogs: [],

      activeTab: 0, // 0 = Email Log, 1 = Whatsapp Log
    }
  },

  watch: {
    modal: {
      immediate: true,
      handler(newValue) {
        this.isModalOpen = newValue
        if (newValue) {
          this.fetchLogs()
          this.fetchWhatsappLogs()
        }
      },
    },
    isModalOpen(newValue) {
      this.$emit('update:modal', newValue)
    },
  },

  methods: {
    debounceFetchLogs: debounce(function () {
      this.fetchLogs()
      this.fetchWhatsappLogs()
    }, 500),

    async fetchLogs() {
      this.loading = true
      const startDate = this.dateRange[0]
        ? moment(this.dateRange[0]).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      const endDate = this.dateRange[1]
        ? moment(this.dateRange[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')

      const params = {
        page: this.currentPage,
        limit: this.perPage,
        search: this.filter,
        filters: [`created_at gte '${startDate}'`, `created_at lte '${endDate}'`],
      }

      try {
        const response = await emailLogApi
          .get(params, this.selectedServerRegion)
          .catch((error) => {
            this.handleError(error)
          })

        if (!response.data) return

        this.emailLogs = response.data.paginated_result
        this.filteredItems = response.data.paginated_result
        this.totalRows = response.metadata.total_count
        this.totalPages = response.metadata.total_page
        this.currentPage = response.metadata.page

        this.loading = false
      } catch (error) {
        this.$toast.error('Error loading logs')
      } finally {
        this.loading = false
      }
    },

    resetModal() {
      this.filter = ''
      this.currentPage = 1
      this.currentPageWhatsapp = 1
      this.emailLogs = []
      this.whatsappLogs = []
    },

    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          localStorage.removeItem('token')
          localStorage.removeItem('tokenAccess')
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    changePage(e) {
      this.currentPage = e
      this.fetchLogs()
    },

    resetFilter(newPerPage) {
      this.perPage = newPerPage
      this.currentPage = 1
      this.fetchLogs()
    },

    changePageWhatsapp(e) {
      this.currentPageWhatsapp = e
      this.fetchWhatsappLogs()
    },

    resetFilterWhatsapp(newPerPage) {
      this.perPageWhatsapp = newPerPage
      this.currentPageWhatsapp = 1
      this.fetchWhatsappLogs()
    },

    async onServerRegionChange() {
      this.currentPage = 1
      this.perPage = 10
      this.fetchLogs()
      this.fetchWhatsappLogs()
    },

    async fetchWhatsappLogs() {
      this.loading = true
      const startDate = this.dateRange[0]
        ? moment(this.dateRange[0]).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      const endDate = this.dateRange[1]
        ? moment(this.dateRange[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')

      const params = {
        page: this.currentPageWhatsapp,
        limit: this.perPageWhatsapp,
        search: this.filter,
        filters: [`data_timestamp gte '${startDate}'`, `data_timestamp lte '${endDate}'`],
      }

      try {
        const response = await halosisWhatsappLogApi.get(params, this.selectedServerRegion)

        if (!response?.data) return

        this.whatsappLogs = response.data.paginated_result
        this.filteredItemsWhatsapp = response.data.paginated_result
        this.totalRowsWhatsapp = response.metadata.total_count
        this.totalPagesWhatsapp = response.metadata.total_page
        this.currentPageWhatsapp = response.metadata.page

        this.loading = false
      } catch (error) {
        this.$toast.error('Error loading logs')
      } finally {
        this.loading = false
      }
    },

    whatsappStatusText(code) {
      const mapping = {
        S: 'Sent',
        D: 'Delivered',
        R: 'Read',
        F: 'Failed',
      }
      return mapping[code] || '-'
    },
  },

  mounted() {
    moment.locale('id')
  },
}
</script>

<template>
  <b-modal
    v-model="isModalOpen"
    id="email-log-modal"
    title="Customer Log"
    size="xl"
    :hide-footer="true"
    @hidden="resetModal"
  >
    <div v-if="loading" class="text-center py-3">
      <b-spinner class="align-middle" small></b-spinner>
      <p class="mt-2">Loading email logs...</p>
    </div>

    <div v-else>
      <div class="row mb-3">
        <div class="d-inline-flex col-md-6 d-flex align-items-center">
          <b-form-select
            class="form-select form-select-md __limit--dropdown me-2"
            v-model="selectedServerRegion"
            @change="onServerRegionChange"
          >
            <b-form-select-option value="SG">Singapura</b-form-select-option>
            <b-form-select-option value="ID">Indonesia</b-form-select-option>
          </b-form-select>
          <date-picker
            v-model="dateRange"
            :range="true"
            :clearable="true"
            format="DD/MM/YYYY"
            placeholder="Select Date"
            @change="debounceFetchLogs"
          />
        </div>
        
        <div class="col-md-2 d-flex"></div>

        <div class="col-md-4 d-flex justify-content-end">
          <label class="d-inline-flex align-items-center mc-table-view__search me-2">
            <i class="mc-fa fas fa-search"></i>
            <b-form-input
              v-model="filter"
              :placeholder="$t('global.search') + '...'"
              @input="debounceFetchLogs"
              class="ml-auto ms-2"
            />
          </label>
        </div>
      </div>

      <div class="card-body">
        <b-tabs v-model="activeTab" content-class="p-3 text-muted">
          <b-tab class="border-0">
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-home"></i>
              </span>
              <span class="d-none d-sm-inline-block">User Log</span>
            </template>
            <div class="table-responsive my-3 border border-1">
              <b-table
                class="datatables"
                :items="emailLogs"
                :fields="fields"
                :busy="loading"
                show-empty
                responsive="sm"
                hover
                :empty-text="$t('global.emptyTable')"
                :empty-filtered-text="$t('global.emptyFilteredTable')"
              >
                <template #head(expired_status)="data">
                  <p>
                    {{ data.label }}

                    <i id="expired-status-header-icon" class="mc-fa icon-fill_info"></i>

                    <b-tooltip
                      target="expired-status-header-icon"
                      triggers="hover"
                      placement="bottom"
                    >
                      <p class="text-start">{{ $t('domain.tooltipLog.text1') }}</p>
                      <p class="text-start">{{ $t('domain.tooltipLog.text2') }}</p>
                      <p class="text-start">{{ $t('domain.tooltipLog.text3') }}</p>
                    </b-tooltip>
                  </p>
                </template>
                <template #cell(created_at)="data">
                  {{ data.item.created_at ? momentDMHms(data.value) : '-' }}
                </template>

                <template #cell(email)="data">
                  <b-link :href="`mailto:${data.item.email}`">{{ data.item.email }}</b-link>
                </template>

                <template #cell(link)="data">
                  <b-link v-if="data.item.link" :href="data.item.link" target="_blank">Link</b-link>
                  <span v-else>-</span>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('show') }}
                    <b-form-select
                      class="form-select form-select-sm"
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                      @change="resetFilter"
                    />
                    {{ $t('entry') }}. {{ $t('show') }}&nbsp;<strong>{{
                      filteredItems.length > 0 ? (currentPage - 1) * perPage + 1 : 0
                    }}</strong>
                    &nbsp;{{ $t('to') }}&nbsp;<strong>{{
                      currentPage != totalPages
                        ? currentPage * perPage
                        : (currentPage - 1) * perPage + filteredItems.length
                    }}</strong>
                    &nbsp;{{ $t('from') }}&nbsp;<strong>{{ totalRows }}</strong
                    >&nbsp;{{ $t('entry') }}.
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      @change="changePage"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </b-tab>

          <b-tab class="border-0">
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-home"></i>
              </span>
              <span class="d-none d-sm-inline-block">Whatsapp Log</span>
            </template>
            <div class="table-responsive my-3 border border-1">
              <b-table
                class="datatables"
                :items="whatsappLogs"
                :fields="whatsappLogFields"
                :busy="loading"
                show-empty
                responsive="sm"
                hover
                :empty-text="$t('global.emptyTable')"
                :empty-filtered-text="$t('global.emptyFilteredTable')"
              >
                <template #head(status)="data">
                  <p>
                    {{ data.label }}
                    <i id="status-header-icon" class="mc-fa icon-fill_info"></i>

                    <b-tooltip target="status-header-icon" triggers="hover" placement="bottom">
                      <p class="text-start">{{ $t('domain.whatsappTooltipLog.text1') }}</p>
                      <p class="text-start">{{ $t('domain.whatsappTooltipLog.text2') }}</p>
                      <p class="text-start">{{ $t('domain.whatsappTooltipLog.text3') }}</p>
                      <p class="text-start">{{ $t('domain.whatsappTooltipLog.text4') }}</p>
                    </b-tooltip>
                  </p>
                </template>
                <template #cell(data_timestamp)="data">
                  {{ data.item.data_timestamp ? momentDMHms(data.value) : '-' }}
                </template>

                <template #cell(fullname)="data">
                  {{ data.item.fullname ? data.value : '-' }}
                </template>
                <template #cell(domain_name)="data">
                  {{ data.item.domain_name ? data.value : '-' }}
                </template>

                <template #cell(status)="data">
                  {{ whatsappStatusText(data.item.status) }}
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('show') }}
                    <b-form-select
                      class="form-select form-select-sm"
                      v-model="perPageWhatsapp"
                      size="sm"
                      :options="pageOptionsWhatsapp"
                      @change="resetFilterWhatsapp"
                    />
                    {{ $t('entry') }}. {{ $t('show') }}&nbsp;<strong>{{
                      filteredItemsWhatsapp.length > 0
                        ? (currentPageWhatsapp - 1) * perPageWhatsapp + 1
                        : 0
                    }}</strong>
                    &nbsp;{{ $t('to') }}&nbsp;<strong>{{
                      currentPageWhatsapp != totalPagesWhatsapp
                        ? currentPageWhatsapp * perPageWhatsapp
                        : (currentPageWhatsapp - 1) * perPageWhatsapp + filteredItemsWhatsapp.length
                    }}</strong>
                    &nbsp;{{ $t('from') }}&nbsp;<strong>{{ totalRowsWhatsapp }}</strong
                    >&nbsp;{{ $t('entry') }}.
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPageWhatsapp"
                      :total-rows="totalRowsWhatsapp"
                      :per-page="perPageWhatsapp"
                      @change="changePageWhatsapp"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </b-modal>
</template>
